/**
 * Shared raw-IndexedDB machinery (autosaves-v2 phase 0).
 *
 * Extracted from NoteListAutosaveDb so every IDB database on the origin
 * (`noteListAutosaves`, `hspp-form-autosaves`) shares one hardened connection
 * layer instead of re-growing its own bugs:
 *
 * - Singleton connection per database with a shared in-flight open promise.
 * - 5s open-hang watchdog (warn only; the request is left pending — IDB opens
 *   can legitimately be slow, so we observe rather than abort).
 * - Upgrade-deadlock fix: `onversionchange → close()` and reset, so when a
 *   newer-code tab bumps the version this connection gets out of the way and
 *   the next operation reopens. Without this, `onblocked` fires in the
 *   upgrading tab and the upgrade hangs forever behind any old open tab. If
 *   this tab's pinned version is now older than the upgraded database, the
 *   reopen fails with VersionError — an honest, observable failure (old code
 *   cannot safely use a newer schema) instead of a silent origin-wide hang.
 * - Writes settle at transaction COMMIT, never at request success, so a
 *   rolled-back write is never reported to a caller as saved.
 *
 * This module holds NO telemetry and imports nothing from `cpts/`: libTs stays
 * framework- and app-agnostic. Failure reporting belongs to the callers (the
 * autosave area reports through `logAutosaveHazardEvent`, failure paths only).
 */

export const IDB_OPEN_HANG_TIMEOUT_MS = 5000;

export type SharedIdbConfig = {
  dbName: string;
  version: number;
  /** Runs inside the versionchange transaction; create stores/indexes here. */
  onUpgrade: (db: IDBDatabase, event: IDBVersionChangeEvent) => void;
  /** Prefix for console messages, e.g. 'NoteListAutosaveDb'. */
  logLabel: string;
  /**
   * What an `onblocked` open should do.
   *
   * - `'warn'` (default): warn and leave the request pending — it completes if
   *   the blocking connection goes away.
   * - `'reject'`: fail the open immediately. Required by any caller that gates
   *   rendering on the open settling (`AutosaveContext.tsx` renders children
   *   only when `!idbMode || idbReady`, and only a REJECTED open flips it into
   *   localStorage mode; a pending open would blank the app).
   */
  blockedBehavior?: 'reject' | 'warn';
};

export type SharedIdbConnection = {
  /** Open (or reuse) the singleton connection. */
  open: () => Promise<IDBDatabase>;
  /** Close the connection; the next open() starts fresh. */
  close: () => void;
  /** One-request read transaction on a single store. */
  withRead: <T>(
    storeName: string,
    fn: (store: IDBObjectStore) => IDBRequest<T>
  ) => Promise<T>;
  /** One-request write transaction; settles at COMMIT, not request success. */
  withWrite: (
    storeName: string,
    fn: (store: IDBObjectStore) => IDBRequest
  ) => Promise<void>;
  /**
   * Multi-request/cursor write transaction: `fn` drives the store (cursors,
   * multiple requests) and resolves its own value; the returned promise
   * settles only when the transaction COMMITS. Abort (including quota-at-
   * commit) rejects, never a silent false success.
   *
   * CONSTRAINT — `fn` must issue its FIRST store request synchronously, and
   * every later request from within an earlier request's event handler. An
   * IndexedDB transaction auto-commits as soon as no request is outstanding
   * at the end of the current task, so `fn` must never `await` anything that
   * is not an event of this transaction:
   *
   * ```ts
   * // WRONG - the transaction commits during the await; the put then throws
   * // TransactionInactiveError or is silently lost.
   * withWriteTxn(store, async (s) => { await fetchThing(); s.put(x); });
   *
   * // RIGHT - request issued synchronously; the promise only reports its
   * // outcome, and withWriteTxn still settles on the transaction's commit.
   * withWriteTxn(store, (s) => new Promise((res, rej) => {
   *   const req = s.put(x);
   *   req.onsuccess = () => res(undefined);
   *   req.onerror = () => rej(req.error);
   * }));
   * ```
   *
   * `fn` returns a promise only so it can report ITS outcome; rejecting it
   * aborts the transaction.
   */
  withWriteTxn: <T>(
    storeName: string,
    fn: (store: IDBObjectStore) => Promise<T>
  ) => Promise<T>;
};

export const createSharedIdbConnection = (
  config: SharedIdbConfig
): SharedIdbConnection => {
  const { dbName, version, onUpgrade, logLabel } = config;
  const blockedBehavior = config.blockedBehavior ?? 'warn';

  let dbInstance: IDBDatabase | null = null;
  let dbInitPromise: Promise<IDBDatabase> | null = null;
  // Bumped by every reset. An open request that is still pending when its
  // epoch is superseded has been abandoned (close() mid-open, a blocked
  // reject, an error) and must not install its connection or clobber the
  // state of whatever open started after it.
  let openEpoch = 0;

  const resetConnectionState = () => {
    dbInstance = null;
    dbInitPromise = null;
    openEpoch += 1;
  };

  const open = (): Promise<IDBDatabase> => {
    if (dbInstance) {
      return Promise.resolve(dbInstance);
    }
    if (dbInitPromise) {
      return dbInitPromise;
    }

    const epoch = openEpoch;
    // True only if indexedDB.open() itself threw, i.e. the executor failed
    // before dbInitPromise was even assigned. Handled after the assignment.
    let threwSynchronously = false;

    dbInitPromise = new Promise((resolve, reject) => {
      const hangTimeout = setTimeout(() => {
        console.error(
          `[${logLabel}] TIMEOUT - IndexedDB request hung for ${IDB_OPEN_HANG_TIMEOUT_MS / 1000} seconds. Try clearing site data.`
        );
      }, IDB_OPEN_HANG_TIMEOUT_MS);

      /** This open was superseded while its request was still pending. */
      const isAbandoned = () => epoch !== openEpoch;

      let request: IDBOpenDBRequest;
      try {
        // Safari private windows throw SYNCHRONOUSLY from open(). Reject, and
        // let the post-assignment cleanup below drop the rejected promise so
        // the NEXT load/save retries instead of reusing this failure forever.
        request = indexedDB.open(dbName, version);
      } catch (error) {
        clearTimeout(hangTimeout);
        console.error(`[${logLabel}] OPEN THREW:`, error);
        threwSynchronously = true;
        reject(
          error instanceof Error
            ? error
            : new Error(`Failed to open IndexedDB: ${String(error)}`)
        );
        return;
      }

      request.onerror = () => {
        clearTimeout(hangTimeout);
        console.error(`[${logLabel}] ERROR:`, request.error);
        // An abandoned request must not reset state a newer open now owns.
        if (!isAbandoned()) {
          resetConnectionState();
        }
        reject(new Error(`Failed to open IndexedDB: ${request.error?.message}`));
      };

      request.onblocked = () => {
        console.warn(
          `[${logLabel}] BLOCKED - close other tabs with this app and retry`
        );
        if (blockedBehavior === 'reject' && !isAbandoned()) {
          clearTimeout(hangTimeout);
          resetConnectionState();
          reject(new Error(`IndexedDB open blocked (${dbName})`));
        }
      };

      request.onsuccess = () => {
        clearTimeout(hangTimeout);
        const db = request.result;

        // The blocked-reject path and close()-mid-open both leave this request
        // pending on purpose. If it succeeds afterwards, the caller has long
        // since been told the open failed (or been handed a newer connection),
        // so close this one rather than installing it — otherwise two live
        // connections race and the abandoned one wins the assignment.
        if (isAbandoned()) {
          console.warn(
            `[${logLabel}] ABANDONED OPEN - closing a connection whose open was already given up on`
          );
          db.close();
          reject(new Error(`IndexedDB open abandoned (${dbName})`));
          return;
        }

        dbInstance = db;

        db.onclose = () => {
          // Only if this is still the live connection; a closed-out predecessor
          // must not wipe the state of its replacement.
          if (dbInstance === db) {
            resetConnectionState();
          }
        };

        // Upgrade-deadlock fix: another tab is opening this database at a
        // higher version. Close so its upgrade can proceed; the next operation
        // here reopens (and surfaces VersionError if our code is now too old).
        db.onversionchange = () => {
          console.warn(
            `[${logLabel}] VERSIONCHANGE - closing so another tab's upgrade can proceed`
          );
          if (dbInstance === db) {
            resetConnectionState();
          }
          db.close();
        };

        resolve(db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        onUpgrade(db, event);
      };
    });

    if (threwSynchronously) {
      // Hand this caller the rejection, but clear the singleton so a transient
      // throw (Safari private mode, storage disabled mid-session) doesn't
      // poison every later open for the life of the page.
      const failedOpen = dbInitPromise;
      resetConnectionState();
      return failedOpen;
    }

    return dbInitPromise;
  };

  const close = (): void => {
    if (dbInstance) {
      const staleDb = dbInstance;
      resetConnectionState();
      staleDb.close();
    } else {
      // Nothing open yet, but an open may be in flight. Resetting bumps the
      // epoch, which is what makes that pending request abandon itself (and
      // close the connection it eventually gets) instead of installing it.
      resetConnectionState();
    }
  };

  const withRead = async <T>(
    storeName: string,
    fn: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> => {
    const db = await open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = fn(store);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  };

  // `fn` must issue its first store request synchronously and chain any
  // further ones from request event handlers — see the withWriteTxn doc on
  // SharedIdbConnection. Awaiting non-IDB work inside `fn` lets the
  // transaction auto-commit underneath it.
  //
  // Write transactions settle at COMMIT, not at request success: in real
  // browsers a quota failure often surfaces as a transaction abort AFTER the
  // put request has already fired onsuccess. Resolving early would report a
  // rolled-back write as saved — the exact silent-save-failure this module
  // exists to make observable.
  const withWriteTxn = async <T>(
    storeName: string,
    fn: (store: IDBObjectStore) => Promise<T>
  ): Promise<T> => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      let result: T;
      let fnError: unknown;
      let fnFailed = false;

      fn(store).then(
        (value) => {
          result = value;
        },
        (error) => {
          fnFailed = true;
          fnError = error;
          try {
            transaction.abort();
          } catch {
            // Already aborting/finished.
          }
        }
      );

      transaction.oncomplete = () => resolve(result);
      transaction.onabort = () => {
        const error = fnFailed ? fnError : transaction.error;
        const errorName =
          (error as DOMException | null)?.name ??
          (fnFailed ? 'CallbackError' : 'AbortError');
        console.error(
          `[${logLabel}] WRITE FAILED - transaction aborted (${errorName})`
        );
        reject(error ?? new Error(`IndexedDB transaction aborted (${dbName})`));
      };
    });
  };

  const withWrite = (
    storeName: string,
    fn: (store: IDBObjectStore) => IDBRequest
  ): Promise<void> =>
    withWriteTxn(storeName, (store) => {
      return new Promise<void>((resolve, reject) => {
        const request = fn(store);
        // Resolution is gated on the transaction's commit in withWriteTxn;
        // this inner promise only propagates request-level failures.
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });

  return { open, close, withRead, withWrite, withWriteTxn };
};
